import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FormDialog, Field } from "./form-dialog";
import { nameField, isoDateNotPast, notesField } from "@/lib/validators";

const schema = z.object({
  client: nameField(120),
  category: z.enum(["Editorial", "Commercial", "Portrait", "Wedding", "Product", "Lifestyle"]),
  date: isoDateNotPast,
  budget: z.coerce.number().int("Whole numbers only").min(100, "Min $100").max(1_000_000, "Max $1,000,000"),
  status: z.enum(["new", "pending", "booked", "archived"]),
  notes: notesField,
});
type Values = z.infer<typeof schema>;

export function NewInquiryDialog() {
  const { register, handleSubmit, formState: { errors, isDirty }, setValue, watch, reset } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { client: "", category: "Commercial", date: "", budget: 0, status: "new", notes: "" },
  });
  return (
    <FormDialog
      trigger={<Button size="sm" className="gap-1.5"><Plus className="size-3.5" /> New inquiry</Button>}
      title="Log a new inquiry"
      description="Capture a lead. Convert to a booking once the brief is confirmed."
      submitLabel="Save inquiry"
      isDirty={isDirty}
      onReset={() => reset()}
      onSubmit={() => new Promise<void>((resolve, reject) => {
        handleSubmit((v) => {
          toast.success("Inquiry logged", { description: `${v.client} · ${v.category}` });
          resolve();
        }, () => reject())();
      })}
    >
      <Field label="Client / company" htmlFor="iq-client" error={errors.client?.message}>
        <Input id="iq-client" placeholder="Marcus Halden" {...register("client")} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Category" error={errors.category?.message}>
          <Select value={watch("category")} onValueChange={(v) => setValue("category", v as Values["category"])}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {["Editorial","Commercial","Portrait","Wedding","Product","Lifestyle"].map((c) =>
                <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Status" error={errors.status?.message}>
          <Select value={watch("status")} onValueChange={(v) => setValue("status", v as Values["status"])}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="booked">Booked</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Requested date" htmlFor="iq-date" error={errors.date?.message}>
          <Input id="iq-date" type="date" {...register("date")} />
        </Field>
        <Field label="Budget (USD)" htmlFor="iq-budget" error={errors.budget?.message}>
          <Input id="iq-budget" type="number" min={0} step={100} {...register("budget")} />
        </Field>
      </div>
      <Field label="Notes" htmlFor="iq-notes" error={errors.notes?.message}>
        <Textarea id="iq-notes" rows={3} placeholder="Brief, references, deliverables…" {...register("notes")} />
      </Field>
    </FormDialog>
  );
}
